import React from 'react';
import { Trophy, Users, CalendarDays, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import { Tournament, Config, Team, Player, UserProfile } from '../../types';

interface Props {
  key?: string | number;
  t: Tournament;
  config: Config;
  allTeams: Team[];
  players: Player[];
  sedesPublicas?: UserProfile[];
  allUsers?: UserProfile[];
  onClick: () => void;
  onEditChampion?: (t: Tournament) => void;
  onDelete?: (t: Tournament) => void;
}

export function AdminArchivedTournamentRow({ t, config, allTeams, players, sedesPublicas = [], allUsers = [], onClick, onEditChampion, onDelete }: Props) {
  const clubOrganizer = sedesPublicas.find(s => s.uid === t.organizerId) || allUsers?.find(u => u.uid === t.organizerId);
  const playerOrganizer = players.find(p => p.id === t.organizerId);
  
  const isSuperAdminOrganizer = clubOrganizer?.role === 'superadmin' || 
    t.organizerId === 'admin' || 
    t.organizerId === 'main_club' || 
    t.organizerId === 'agunavarro1989@gmail.com' || 
    allUsers?.find(u => u.uid === t.organizerId)?.role === 'superadmin' ||
    allUsers?.find(u => u.uid === t.organizerId)?.email === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.email === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.name?.toLowerCase().includes('agustin navarro');

  const organizerName = isSuperAdminOrganizer ? config.pageName : (playerOrganizer?.organizerProfile?.name || clubOrganizer?.name || playerOrganizer?.name || t.organizerName || 'Torneo');
  const logoUrl = isSuperAdminOrganizer ? config.logoUrl : (playerOrganizer?.organizerProfile?.logoUrl || clubOrganizer?.photoUrl || t.organizerLogo || config.logoUrl);

  const championId = t.championId;
  const championTeam = allTeams.find(tm => tm.id === championId);
  const getPlayerLastName = (pId?: string) => {
    if (!pId) return '';
    const player = players.find(p => p.id === pId);
    if (!player) return '';
    const parts = player.name.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  };

  const p1LastName = getPlayerLastName(championTeam?.player1Id);
  const p2LastName = getPlayerLastName(championTeam?.player2Id);
  const championName = championTeam ? `${p1LastName} / ${p2LastName}` : null;

  return (
    <div 
      onClick={onClick}
      className="max-w-4xl mx-auto w-full bg-slate-800/40 hover:bg-slate-800/80 border border-white/5 hover:border-white/20 rounded-xl p-2.5 md:p-3 flex flex-row items-center gap-3 cursor-pointer transition-all group"
    >
      {/* 1. Organizer Logo */}
      <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 bg-black/40 rounded-xl flex items-center justify-center p-1 border border-white/5 overflow-hidden">
        {logoUrl ? (
          <img 
            src={logoUrl}
            alt={organizerName}
            className="w-full h-full object-contain"
            referrerPolicy="no-referrer"
          />
        ) : (
          <Trophy size={18} className="text-theme-primary" />
        )}
      </div>

      {/* 2. Main Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm md:text-base font-black text-theme-primary uppercase italic leading-none truncate">
            {t.name}
          </h3>
          <span className="hidden sm:inline-block px-1.5 py-0.5 bg-black/40 rounded text-[7px] font-black uppercase tracking-widest text-white/60 border border-white/5 whitespace-nowrap">
            {t.status?.toUpperCase() || 'FINALIZADO'}
          </span>
        </div>
        
        <div className="flex items-center gap-x-1.5 gap-y-0.5 flex-wrap text-[9px] md:text-[10px] text-white/60 font-medium">
          {t.category && <span className="font-bold text-white/90">{t.category.toLowerCase() === 'libre' ? 'CAT. LIBRE' : t.category}</span>}
          {t.gender && (
            <span className="truncate">
              • {t.gender?.toLowerCase() === 'masculino' ? 'Caballeros' :
                  t.gender?.toLowerCase() === 'femenino' ? 'Damas' : 
                  (t.gender?.toLowerCase() === 'sin_restriccion' || t.gender?.toLowerCase() === 'sin restricción') ? 'Libre' : 'Mixto'}
            </span>
          )}
          <span className="hidden xs:inline">• {t.registeredTeamIds?.length || 0}/{t.maxTeams || 16} Equ.</span>
          <span className="truncate">
            • {t.startedAt
              ? `Inicio: ${new Date(t.startedAt).getDate()}-${new Date(t.startedAt).getMonth() + 1}-${new Date(t.startedAt).getFullYear().toString().slice(-2)}`
              : t.startDate ? `Fecha: ${t.startDate}` : 'A definir'}
          </span>
        </div>
      </div>

      {/* 3. Champion / Status */}
      <div className="flex flex-col items-end shrink-0 w-[80px] sm:w-[120px]">
        {championName ? (
          <div className="flex flex-col items-end text-right">
            <div className="flex items-center gap-1 text-yellow-500 mb-0.5">
              <Trophy size={10} />
              <span className="text-[7px] font-black uppercase tracking-widest hidden sm:inline">Campeón</span>
            </div>
            <span className="text-[10px] font-bold text-yellow-100 truncate w-full text-right">{championName}</span>
          </div>
        ) : (
          <span className="px-1.5 py-0.5 bg-black/40 rounded text-[7px] font-black uppercase tracking-widest text-white/60 border border-white/5 sm:hidden">
            {t.status?.toUpperCase() || 'FINALIZADO'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-1">
        {onEditChampion && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditChampion(t);
            }}
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/5 hover:bg-theme-primary/20 flex items-center justify-center text-white/50 hover:text-theme-primary transition-all shrink-0 z-10"
            title="Editar Campeón"
          >
            <Edit2 size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(t);
            }}
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-white/50 hover:text-red-500 transition-all shrink-0 z-10"
            title="Eliminar Torneo"
          >
            <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
        )}
        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white/40 group-hover:text-white/80 shrink-0">
          <ChevronRight size={14} />
        </div>
      </div>
    </div>
  );
}
